import { useMemo } from "react"

import { useParams } from "react-router-dom"

import { GradeBadge } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { Typography } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useFormatApiDateString } from "../../../riskZones/utils/useFormatApiString"
import { useSpeedingsContext } from "../../contexts/SpeedingsContext"
import { useSpeedingsGrades } from "../../hooks/queries/useSpeedingsGrades"
import { useSpeedingsSurveys } from "../../hooks/queries/useSpeedingsSurveys"
import { speedingsLegend } from "../../utils/getSpeedingsConfig"
import { getGradeFromValue } from "../../utils/valueUtils"

import * as Styled from "./SpeedingsGrades.styled"

export function SpeedingsGrades(props) {

    const { className } = props

    const { road } = useParams()
    const format = useFormatApiDateString()

    const {
        data: grades,
        isLoading: isLoadingGrades,
        isError: isErrorGrades,
    } = useSpeedingsGrades({
        group_by: "year",
        ...(road && { linear_location_road: road }),
    })

    const {
        data: surveys,
        isLoading: isLoadingSurveys,
        isError: isSurveysError,
    } = useSpeedingsSurveys()

    const isLoading = isLoadingGrades || isLoadingSurveys
    const isError = isErrorGrades || isSurveysError

    const { t } = useTranslation([I18N_NAMESPACE])
    const [state, dispatch] = useSpeedingsContext()
    const { currentYear } = state
    const { setCurrentYear } = dispatch

    const yearlyStatusesOptions = useMemo(() => {
        if (isLoading || isError || !surveys?.length) {
            return []
        }
        const sortedSurveys = surveys.sort((a, b) => a.year - b.year)
        const firstYear = sortedSurveys[0].year
        const lastYear = sortedSurveys.at(-1).year
        const displayedYears = Array.from({ length: lastYear - firstYear + 1 }, (_, i) => {
            return firstYear + i
        })
        return (displayedYears || []).map(year => {
            const filteredDataItem = grades?.find(item => item.year === year)
            return ({
                date: year,
                badgeComponent: <GradeBadge
                    grade={getGradeFromValue(filteredDataItem?.weighted_avg) ?? null}
                    id={`speedings-${year}`}
                    legend={speedingsLegend}
                />,
                value: { uuid: year },
                uuid: year,
            })
        })
    }, [grades, surveys, isError, isLoading])

    const infoMessage = useMemo(() => {
        if (isLoading || isError || !surveys?.length) {
            return null
        }

        const currentSurvey = surveys.find(item => item.year === currentYear)
        if (!currentSurvey) {
            return t(I18N_NAMESPACE, "containers.speedingsGrades.moduleNotAcquired", { year: currentYear })
        }

        const filteredDataItem = grades?.find(item => item.year === currentYear)
        const currentYearGrade = getGradeFromValue(filteredDataItem?.weighted_avg)
        const formattedDateFrom = format(currentSurvey?.date_from)
        const formattedDateTo = format(currentSurvey?.date_to)

        if (!currentYearGrade && road) {
            return t(I18N_NAMESPACE, "containers.speedingsGrades.noRoadGrade", { year: currentYear })
        } else if (!currentYearGrade && !road) {
            return t(I18N_NAMESPACE, "containers.speedingsGrades.noGrades", { year: currentYear })
        } else {
            return t(I18N_NAMESPACE, "containers.speedingsGrades.infos", { dateFrom: formattedDateFrom, dateTo: formattedDateTo })
        }
    }, [isLoading, isError, surveys, grades, currentYear, road, t, format])

    return <Styled.Container>
        <Styled.YearSelector
            className={className}
            onClick={setCurrentYear}
            yearlyStatuses={yearlyStatusesOptions}
            selectedValue={{ uuid: currentYear }}
        />
        <Typography variant="Desktop & Mobile/Body/Small">
            {infoMessage}
        </Typography>
    </Styled.Container>
}